import React, { useMemo } from 'react'
import { makeStyles } from '@material-ui/styles'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import Tooltip from 'components/common/Tooltip'
import { MARKER_OUTLINE, MARKER_SIZE } from './timeBasedLayout'

const MAX_DISPLAYED_MISSED_COUNT = 99
/**
 * The two rings a missed scan is drawn as, from the design.
 *
 * Hollow where a scan that happened is filled, so absence never reads as an event.
 */
const INNER_RING_SIZE = 13
const INNER_RING_WIDTH = 2
const OUTER_RING_WIDTH = 1

const useStyles = makeStyles(theme => ({
  missedScanMarker: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: MARKER_SIZE,
    height: MARKER_SIZE,
    borderRadius: '100%',
    background: 'var(--bg-color-timelineV2)',
    // Two rings, drawn outside the box rather than as a border, so the inner one keeps the size the
    // design gives it and a gap is left between them. Then the panel-coloured ring every marker
    // carries, so two that overlap still read as two.
    boxShadow: `0 0 0 ${OUTER_RING_WIDTH}px #727B8C, 0 0 0 ${
      OUTER_RING_WIDTH + MARKER_OUTLINE
    }px var(--bg-color-timelineV2)`,
    boxSizing: 'border-box',
    userSelect: 'none'
  },
  // The dark ring inside the outer one. Both are rings rather than a disc, so the marker still
  // reads as a scan that never happened next to the filled ones that did.
  innerRing: {
    width: INNER_RING_SIZE,
    height: INNER_RING_SIZE,
    borderRadius: '100%',
    border: `${INNER_RING_WIDTH}px solid #001335`,
    boxSizing: 'border-box'
  },
  collapsed: {
    width: MARKER_SIZE,
    height: MARKER_SIZE,
    fontFamily: 'Dazzed',
    fontSize: 9,
    fontWeight: 600,
    color: '#727B8C'
  }
}))

/**
 * A scan that was due and never arrived, derived from the patient's cycles.
 *
 * Hollow where a scan that happened is filled, so absence never reads as an event. A gap is drawn
 * across every month it spans; only one longer than the axis can usefully hold collapses into a
 * single counted marker.
 */
const MissedScanMarker = ({ timelineItem }) => {
  const classes = useStyles()
  const { t } = useTranslation()

  const { missedCount, fromDate, toDate, isCollapsed } = timelineItem.payload

  const tooltipValue = useMemo(
    () =>
      isCollapsed
        ? t('pages.patients.selectedPatient.timelineV2.miniTimeline.missedScanTooltip.collapsed', {
            count: missedCount,
            fromDate: moment(fromDate).format('MMM D, YYYY'),
            toDate: moment(toDate).format('MMM D, YYYY')
          })
        : t('pages.patients.selectedPatient.timelineV2.miniTimeline.missedScanTooltip.single', {
            dueDate: moment(fromDate).format('MMM D, YYYY')
          }),
    [isCollapsed, missedCount, fromDate, toDate, t]
  )

  const marker = (
    <div className={[classes.missedScanMarker, isCollapsed ? classes.collapsed : ''].join(' ')}>
      {/* A patient who stopped scanning years ago can run to four figures, which no marker can hold. */}
      {isCollapsed ? (
        missedCount > MAX_DISPLAYED_MISSED_COUNT ? (
          `${MAX_DISPLAYED_MISSED_COUNT}+`
        ) : (
          missedCount
        )
      ) : (
        <div className={classes.innerRing} />
      )}
    </div>
  )

  // Carried even inside a stack: a missed scan is not part of the card's paged sequence, so this
  // is the only thing that says when it was due.
  return (
    <Tooltip placement="right" value={tooltipValue}>
      {marker}
    </Tooltip>
  )
}

export default React.memo(MissedScanMarker)
